import React, { FC, ReactNode, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import {
  MetricsBarChart,
  MetricsLineChart,
  MetricsStackedBarChart,
  formatDurationToMinutes,
} from '@/apps/latest/components/portal/MetricsChart'
import {
  Banner,
  KpiTile,
  PageLoading,
  SubsectionHeader,
  TabRow,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  useMetricsSummaryQuery,
  useMetricsAnalyticsQuery,
  usePharmacyListQuery,
  generateTrendAxis,
  mapDataToDateRange,
} from '@/features/internal/metrics'
import { buildPharmacistTargetParams } from '@/features/internal/metrics'
import { formatPercentage } from '@/shared/utils/formatters'
import { Dayjs } from 'dayjs'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { MetricsTarget } from './TargetSelector'

type PeriodType = 'daily' | 'weekly' | 'monthly'

const PERIOD_TABS: { id: PeriodType; label: string }[] = [
  { id: 'daily', label: '日次' },
  { id: 'weekly', label: '週次' },
  { id: 'monthly', label: '月次' },
]

const ChartFrame: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Stack gap={0.5}>
    <Typography sx={PORTAL_TEXT.cellMeta}>{label}</Typography>
    <Box>{children}</Box>
  </Stack>
)

const formatDurationMinSec = (seconds: number): string =>
  `${Math.floor(seconds / 60)}分${seconds % 60}秒`

interface SummaryTabProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  target: MetricsTarget
}

// 概況タブ: 旧・利用状況 (KPI+分布) / 実績推移 (期間別推移) / 指導分析 (薬剤師軸+発話比率) を
// 「対象セレクタ × 同一セクション構成」に統合した。APIは3画面とも同じ
// summary/analytics だったため、対象パラメータの配線替えのみで等価になる。
export const SummaryTab: FC<SummaryTabProps> = ({
  startDate,
  endDate,
  target,
}) => {
  const [period, setPeriod] = useState<PeriodType>('daily')
  const { pharmacyData } = usePharmacyListQuery()

  const targetParams = useMemo(() => {
    // 'all' は「絞らない」= キーを送らない。本部なら全社、それ以外は自店になる
    if (target === 'all') return {}
    if (target.startsWith('pt:')) {
      return buildPharmacistTargetParams(target.slice(3))
    }
    if (target.startsWith('ph:')) {
      return { pharmacy_ids: [target.slice(3)] }
    }
    return {}
  }, [target])

  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return null
    return {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    }
  }, [startDate, endDate])

  const ready = !!dateRange && !!pharmacyData

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useMetricsSummaryQuery({
    params: {
      date_range: dateRange ?? { start: '', end: '' },
      include_previous_period: true,
      ...targetParams,
    },
    enabled: ready,
  })

  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
  } = useMetricsAnalyticsQuery({
    params: {
      date_range: dateRange ?? { start: '', end: '' },
      period,
      ...targetParams,
    },
    enabled: ready,
  })

  const loading = summaryLoading || analyticsLoading
  const errors = [summaryError?.message, analyticsError?.message].filter(
    Boolean
  )
  const error = errors.length > 0 ? errors.join('、') : null

  const formatMainTrendData = useMemo(() => {
    if (!analytics?.trends || !startDate || !endDate) return []

    const range = generateTrendAxis(period, startDate, endDate, [
      ...(analytics?.trends?.visits ?? []).map((item) => item.date),
      ...(analytics?.trends?.soap_generations ?? []).map((item) => item.date),
      ...(analytics?.trends?.copies ?? []).map((item) => item.date),
    ])

    const visitsMap = new Map<string, number>()
    const soapMap = new Map<string, number>()
    const copyMap = new Map<string, number>()

    analytics?.trends?.visits?.forEach((item) => {
      visitsMap.set(item.date, item.count)
    })
    analytics?.trends?.soap_generations?.forEach((item) => {
      soapMap.set(item.date, item.count)
    })
    analytics?.trends?.copies?.forEach((item) => {
      copyMap.set(item.date, item.count)
    })

    return range.map(({ dateStr, displayDate }) => {
      const visitCount = visitsMap.get(dateStr) || 0
      const soapCount = soapMap.get(dateStr) || 0
      const copyCount = copyMap.get(dateStr) || 0

      return {
        日付: displayDate,
        コピー実施: copyCount,
        SOAP生成のみ: soapCount - copyCount,
        来局のみ: visitCount - soapCount,
      }
    })
  }, [analytics?.trends, startDate, endDate, period])

  const formatDurationTrendData = useMemo(() => {
    if (!analytics?.trends?.durations || !startDate || !endDate) return []

    const range = generateTrendAxis(
      period,
      startDate,
      endDate,
      (analytics?.trends?.durations ?? []).map((item) => item.date)
    )
    const data = mapDataToDateRange(
      range,
      analytics?.trends?.durations,
      (item) => item.avg_duration || 0,
      0
    )

    return data.map((item) => ({
      日付: item.日付,
      平均時間: item.value,
    }))
  }, [analytics?.trends?.durations, startDate, endDate, period])

  const formatExchangeTrendData = useMemo(() => {
    if (!analytics?.trends?.exchange_counts || !startDate || !endDate) return []

    const range = generateTrendAxis(
      period,
      startDate,
      endDate,
      (analytics?.trends?.exchange_counts ?? []).map((item) => item.date)
    )
    const data = mapDataToDateRange(
      range,
      analytics?.trends?.exchange_counts,
      (item) => item.avg_count || 0,
      0
    )

    return data.map((item) => ({
      日付: item.日付,
      平均回数: item.value,
    }))
  }, [analytics?.trends?.exchange_counts, startDate, endDate, period])

  const formatExchangeDistribution = () => {
    if (!analytics?.distributions.exchange_count) return []
    return Object.entries(analytics.distributions.exchange_count).map(
      ([range, count]) => ({
        やりとり回数: range + '回',
        件数: count,
      })
    )
  }

  const formatMedicationData = () => {
    if (!analytics?.distributions.medication_count_pattern) return []
    return Object.entries(analytics.distributions.medication_count_pattern).map(
      ([range, pattern]) => ({
        薬剤数範囲: range + '剤',
        平均指導時間: pattern.avg_duration,
      })
    )
  }

  const formatHourlyPattern = () => {
    if (!analytics?.distributions.hourly_pattern) return []

    const timeLabels: Record<string, string> = {
      '00-09': '0-9時',
      '09-12': '9-12時',
      '12-15': '12-15時',
      '15-18': '15-18時',
      '18-24': '18-24時',
    }

    const timeOrder = ['00-09', '09-12', '12-15', '15-18', '18-24']

    return timeOrder.map((timeRange) => {
      const data = analytics.distributions.hourly_pattern[timeRange] || {
        count: 0,
        avg_duration: 0,
      }
      return {
        時間帯: timeLabels[timeRange] || timeRange,
        平均指導時間: data.avg_duration,
        件数: data.count,
      }
    })
  }

  return (
    <Stack gap={4}>
      {loading && <PageLoading />}
      {error && <Banner severity="danger">{error}</Banner>}

      {summary && (
        <Box>
          <SubsectionHeader heading="基本指標" />
          <Grid container columnSpacing={0} rowSpacing={1}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile title="来局数" value={summary.total_count} unit="件" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile
                title="SOAP生成数"
                value={`${summary.soap_count || 0}`}
                unit={`件 (${summary.soap_rate?.toFixed(1) || 0}%)`}
                divider
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile
                title="コピー数"
                value={`${summary.copy_count || 0}`}
                unit={`件 (${summary.copy_rate?.toFixed(1) || 0}%)`}
                divider
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile
                title="平均指導時間"
                value={formatDurationMinSec(summary.average_duration)}
                delta={
                  summary.previous_period &&
                  summary.previous_period.average_duration > 0
                    ? {
                        value: `前月 ${formatDurationMinSec(
                          summary.previous_period.average_duration
                        )}`,
                        direction: 'flat',
                      }
                    : undefined
                }
                divider
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile
                title="1剤あたり時間"
                value={summary.time_per_medication?.toFixed(1) || '-'}
                unit="秒/剤"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile
                title="平均やりとり回数"
                value={summary.average_exchange_count.toFixed(1)}
                unit="回"
                divider
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiTile
                title="話速"
                value={
                  summary.speech_rate !== null
                    ? summary.speech_rate.toFixed(0)
                    : '-'
                }
                unit={summary.speech_rate !== null ? '文字/分' : undefined}
                divider
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {summary?.speech_ratio && (
        <Box>
          <SubsectionHeader heading="発話比率" />
          {/* 灰2階調のバー + 値は下のキャプション1行 (バー内白抜き・青塗りは配色規律違反) */}
          <Stack gap={0.75}>
            <Box
              sx={{
                display: 'flex',
                height: 12,
                borderRadius: `${RADIUS.md}px`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${summary.speech_ratio.pharmacist_ratio}%`,
                  bgcolor: colorTokens.fg.faint,
                }}
              />
              <Box
                sx={{
                  width: `${summary.speech_ratio.patient_ratio}%`,
                  bgcolor: colorTokens.bg.subtle,
                }}
              />
            </Box>
            <Typography sx={PORTAL_TEXT.cellMeta}>
              薬剤師 {formatPercentage(summary.speech_ratio.pharmacist_ratio)}%
              ・ 患者 {formatPercentage(summary.speech_ratio.patient_ratio)}%
            </Typography>
          </Stack>
        </Box>
      )}

      {analytics && (
        <>
          <Box>
            <SubsectionHeader
              heading="推移"
              actions={
                <TabRow
                  tabs={PERIOD_TABS}
                  active={period}
                  onChange={setPeriod}
                />
              }
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartFrame label="来局数・SOAP生成・コピー数推移">
                  {/* ファネルの最終成果=コピー実施のみ緑の塗り、残りは灰階調 (design-book 04-apps/kakeru.md 統計節) */}
                  <MetricsStackedBarChart
                    data={formatMainTrendData}
                    dataKeys={['コピー実施', 'SOAP生成のみ', '来局のみ']}
                    xAxisKey="日付"
                    height={350}
                    colors={[
                      colorTokens.state.successFg,
                      colorTokens.fg.faint,
                      colorTokens.bg.subtle,
                    ]}
                    graphStackBottom="first"
                    legendReverse={true}
                    tooltipReverse={true}
                    yAxisUnit="件"
                  />
                </ChartFrame>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartFrame label="平均指導時間の推移">
                  <MetricsLineChart
                    data={formatDurationTrendData}
                    dataKey="平均時間"
                    xAxisKey="日付"
                    height={350}
                    color={colorTokens.fg.muted}
                    yAxisTickFormatter={formatDurationToMinutes}
                  />
                </ChartFrame>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartFrame label="平均やりとり回数の推移">
                  <MetricsLineChart
                    data={formatExchangeTrendData}
                    dataKey="平均回数"
                    xAxisKey="日付"
                    height={350}
                    color={colorTokens.fg.muted}
                    yAxisUnit="回"
                  />
                </ChartFrame>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <SubsectionHeader heading="業務パターン分析" />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartFrame label="時間帯別の平均指導時間">
                  <MetricsBarChart
                    data={formatHourlyPattern()}
                    dataKey="平均指導時間"
                    xAxisKey="時間帯"
                    height={250}
                    color={colorTokens.fg.muted}
                    yAxisTickFormatter={formatDurationToMinutes}
                  />
                </ChartFrame>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartFrame label="薬剤数別の平均指導時間">
                  <MetricsBarChart
                    data={formatMedicationData()}
                    dataKey="平均指導時間"
                    xAxisKey="薬剤数範囲"
                    height={250}
                    color={colorTokens.fg.muted}
                    yAxisTickFormatter={formatDurationToMinutes}
                  />
                </ChartFrame>
              </Grid>
              {/* 旧「分布分析」節 (チャート1本) はここへ合流 — 見出し1つ分のエントロピー削減 */}
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartFrame label="患者とのやりとり回数の分布">
                  <MetricsBarChart
                    data={formatExchangeDistribution()}
                    dataKey="件数"
                    xAxisKey="やりとり回数"
                    height={250}
                    color={colorTokens.fg.muted}
                  />
                </ChartFrame>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Stack>
  )
}
